<template>
  <ScrollArea>
    <div
      class="settings--limit-width">
      <h1 class="settings__title">Change password</h1>

      <div
        class="settings--box big" >
        <ValidationObserver
          ref="customFieldObserver"
          v-slot="{ valid, dirty, pristine }" >

          <b-form-group>
            <label class="flex flex-align-center">
              Current password
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="Current password" >
              <b-input
                v-model="currentPasswordModel"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter your current password"
                type="password" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>

          <b-form-group>
            <label class="flex flex-align-center">
              New password
            </label>
            <ValidationProvider
              ref="newPasswordProviderRef"
              v-slot="{ errors }"
              rules="required|min:8"
              name="New password" >
              <b-input
                v-model="newPasswordModel"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter your new password"
                type="password" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>

          <b-form-group>
            <label class="flex flex-align-center">
              Confirm new password
            </label>
            <ValidationProvider
              ref="newPasswordConfirmProviderRef"
              v-slot="{ errors }"
              :rules="{
                required: true,
                matchMe: {
                  matchTo: newPasswordModel,
                  message: 'Passwords do not match.',
                }
              }"
              name="New password" >
              <b-input
                v-model="newPasswordConfirmModel"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter your new password"
                type="password" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>

            <div
              v-if="wrongPassword"
              class="mt-4 error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ wrongPassword }}</span>
            </div>
          </b-form-group>

          <FooterActions
            :form-valid="valid"
            :has-changes="dirty"
            :show-cancel="!pristine"
            :save-busy="saving"
            @reset="resetChanges"
            @save="updateNotificationSettings" />
        </ValidationObserver>
      </div>

    </div>
  </ScrollArea>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { extend } from 'vee-validate';
import { min } from 'vee-validate/dist/rules';

import ScrollArea from '@/components/common/ScrollArea';
import ToasterMixin from '@/mixins/Toaster';
import FooterActions from '@/components/business/settings/partials/FooterActions';

extend('min', {
  ...min,
  message: 'Minimum 8 characters required.',
});

export default {
  name: 'Password',
  components: {
    FooterActions,
    ScrollArea,
  },
  mixins: [
    ToasterMixin,
  ],
  data() {
    return {
      saving: false,
      currentPasswordModel: null,
      newPasswordModel: null,
      newPasswordConfirmModel: null,
      wrongPassword: false,
    };
  },
  computed: {
    ...mapGetters({
      apiError: 'automation/apiError',
    }),
  },
  watch: {
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.showToaster(error.data.errors, null, null, 'error');
      } else {
        this.showToaster(error.message || 'Something went wrong. Please try again.', null, null, 'error');
      }
    },
    currentPasswordModel() {
      this.wrongPassword = false;
    },
  },
  mounted() {
    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      updateMemberPassword: 'user/updateMemberPassword',
    }),
    resetChanges() {
      this.currentPasswordModel = null;
      this.newPasswordModel = null;
      this.newPasswordConfirmModel = null;
      this.$nextTick(() => {
        this.$refs.newPasswordProviderRef.reset();
        this.$refs.newPasswordConfirmProviderRef.reset();
        this.$refs.customFieldObserver.reset();
      });
    },
    updateNotificationSettings() {
      this.saving = true;
      const payload = {
        password: this.currentPasswordModel,
        new_password: this.newPasswordModel,
      };

      this.wrongPassword = false;

      this.updateMemberPassword(payload)
        .then((response) => {
          if (response.status === 200) {
            this.showToaster('Your password has been updated successfully.', null, null, 'OK');
            this.resetChanges();
          }
        })
        .catch((error) => {
          if (error.response.status === 403) {
            this.wrongPassword = error.response.data.message;
          } else {
            this.showToaster('There was an error. Please try again later.', null, null, 'error');
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.avatar-icon {
  margin-bottom: 15px;
}

::v-deep .text-input-component .text-input-wrapper {
    max-height: unset;

    .input-scroll-area {
      height: unset;
      min-height: 72px;
      max-height: calc(100vh - 400px);
    }
}
</style>
